import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaArrowRight, FaCrown, FaCopy, FaEnvelope, FaPhone, FaPaypal, FaTimes } from "react-icons/fa";
import { useQuery } from "react-query";
import LoadingScreen from "../../components/LoadingScreen";
import { API_URL } from "../../url/api";
import { Availability, SlotProps, fetchAvailabiltyData } from "./hooks";
import { DoctorType, SlotType } from "./types";
import { data } from "autoprefixer";
import BookingReceiptModal from "./BookingReceiptModal";


interface FreeSlotAllocation {
  id: number;
  doctor: {
    id: number;
    name: string;
  };
  remaining_slots: number;
  expiration_date: string;
}

const BookingStepper = () => {
  const [selectedDoctor, setSelectedDoctor] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<{ [date: string]: SlotType[] }>({});
  const [totalCost, setTotalCost] = useState(0);
  const [comment, setComment] = useState("");
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [freeSlotAllocations, setFreeSlotAllocations] = useState<FreeSlotAllocation[]>([]);
  const [doctorFreeSlots, setDoctorFreeSlots] = useState<{ [key: number]: number }>({});
  const [isFreeSlot, setIsFreeSlot] = useState(false);
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(false);
  const [showBookingId, setShowBookingId] = useState("");

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    data: doctorsData,
    isLoading,
    isError,
  } = useQuery("doctors", fetchAvailabiltyData);

  const closeModal = () => {
    setShowPaymentModal(false);
  };

  useEffect(() => {
    if (submissionComplete && !isFreeSlot) {
      setShowPaymentModal(true);
    }
  }, [submissionComplete, isFreeSlot]);

  useEffect(() => {
    const freeSlots = freeSlotAllocations.reduce((acc, allocation) => {
      acc[allocation.doctor.id] = (acc[allocation.doctor.id] || 0) + allocation.remaining_slots;
      return acc;
    }, {} as { [key: number]: number });
    setDoctorFreeSlots(freeSlots);
  }, [freeSlotAllocations]);

  useEffect(() => {
    const fetchFreeSlotAllocations = async () => {
      const userId = localStorage.getItem("userId");
      try {
        const response = await axios.get(`${API_URL}free-slot-allocations/for_user/?user_id=${userId}`);
        setFreeSlotAllocations(response.data);
      } catch (error) {
        console.error("Error fetching free slot allocations:", error);
      }
    };

    fetchFreeSlotAllocations();
  }, []);

  const getNext7Days = () => {
    const today = new Date();
    const next7Days = [];

    for (let i = 0; i < 7; i++) {
      const nextDay = new Date(today);
      nextDay.setDate(today.getDate() + i);
      next7Days.push(nextDay.toISOString().split("T")[0]);
    }

    return next7Days;
  };

  const handleSlotSelection = async (doctor: DoctorType, date: string, slot: SlotType) => {
    const freeAllocation = freeSlotAllocations.find(
      (allocation) => allocation.doctor.id === doctor.id && allocation.remaining_slots > 0
    );

    if (freeAllocation) {
      try {
        await handleFreeSlotBooking(freeAllocation.id, slot.id);
        setSelectedDoctor(doctor.id);
        setSelectedSlots((prevSelectedSlots) => {
          const updatedSlots = { ...prevSelectedSlots };
          if (updatedSlots[date]) {
            const slotIndex = updatedSlots[date].findIndex((s) => s.id === slot.id);
            if (slotIndex > -1) {
              updatedSlots[date].splice(slotIndex, 1);
              if (updatedSlots[date].length === 0) {
                delete updatedSlots[date];
                setSelectedDate(null);
              }
            } else {
              updatedSlots[date].push(slot);
              setSelectedDate(date);
            }
          } else {
            updatedSlots[date] = [slot];
            setSelectedDate(date);
          }
          return updatedSlots;
        });
        setFreeSlotAllocations((prevAllocations) =>
          prevAllocations.map((allocation) =>
            allocation.id === freeAllocation.id
              ? { ...allocation, remaining_slots: allocation.remaining_slots - 1 }
              : allocation
          )
        );
        setDoctorFreeSlots((prev) => ({
          ...prev,
          [doctor.id]: prev[doctor.id] - 1,
        }));
      } catch (error) {
        console.error("Error booking free slot:", error);
      }
    } else {
      setSelectedDoctor(doctor.id);
      setSelectedSlots((prevSelectedSlots) => {
        const updatedSlots = { ...prevSelectedSlots };
        if (updatedSlots[date]) {
          const slotIndex = updatedSlots[date].findIndex((s) => s.id === slot.id);
          if (slotIndex > -1) {
            updatedSlots[date].splice(slotIndex, 1);
            if (updatedSlots[date].length === 0) {
              delete updatedSlots[date];
              setSelectedDate(null);
            }
          } else {
            updatedSlots[date].push(slot);
            setSelectedDate(date);
          }
        } else {
          updatedSlots[date] = [slot];
          setSelectedDate(date);
        }
        return updatedSlots;
      });
    }
  };

  const handleFreeSlotBooking = async (allocationId: number, slotId: number) => {
    try {
      const response = await axios.post(`${API_URL}free-slot-allocations/${allocationId}/use_free_slot/`, {
        slot_id: slotId,
      });
      console.log("Free slot booked:", response.data);
    } catch (error) {
      console.error("Error booking free slot:", error);
      throw error;
    }
  };

  useEffect(() => {
    const selectedDoctorData = doctorsData?.find((doctor) => doctor.id === selectedDoctor);
    if (selectedDoctorData) {
      const slotPrice = selectedDoctorData.price || 100;
      let totalSlots = 0;
      for (const slots of Object.values(selectedSlots)) {
        totalSlots += slots.length;
      }
      const numberOfPaidSlots = Math.floor(totalSlots / 5) * 4 + (totalSlots % 5);
      setTotalCost(numberOfPaidSlots * slotPrice);
    }
  }, [selectedSlots, doctorsData, selectedDoctor]);

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem("userId") || "1";
      setIsSubmitting(true);

      const isFreeSlot = freeSlotAllocations.some(
        (allocation) => allocation.doctor.id === selectedDoctor && allocation.remaining_slots > 0
      );

      if (isFreeSlot) {
        const response = await axios.post(`${API_URL}book/free-appointment/`, {
          doctor: selectedDoctor,
          user: parseInt(userId),
          slot: Object.values(selectedSlots).flat().map((slot) => slot.id),
        });

        if (!response.data.success) {
          throw new Error("Error booking free slots");
        }
      } else {
        const response = await axios.post(`${API_URL}book/appointment/`, {
          doctor: selectedDoctor,
          user: parseInt(userId),
          slot: Object.values(selectedSlots).flat().map((slot) => slot.id),
          amount: totalCost * 100,
        });

        if (!response.data.success) {
          throw new Error("Error booking appointments");
        }

        setSubmissionComplete(true);
        if (!isFreeSlot) {
          setShowPaymentInstructions(true);
          setShowBookingId(response?.data.sessions.join(", "));
        }
      }


    } catch (error) {
      console.error("Error submitting booking:", error);
      alert("There was an error submitting your booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modifiyAvailabilityList = (availability: Availability[]) => {
    const result = availability.reduce((acc: SlotProps[], item) => {
      return acc.concat(item.slots);
    }, []);
    return groupSlotsByDate(result);
  };

  const groupSlotsByDate = (slots: SlotProps[]) => {
    const result = slots.reduce<{ [key: string]: SlotProps[] }>((acc: any, slot) => {
      const date = new Date(slot.datetime).toLocaleDateString("en-CA");
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(slot);
      return acc;
    }, {});

    return Object.entries(result).map(([date, slots], index) => ({
      id: index + 1,
      date,
      slots,
    }));
  };

  const filteredDoctorsData =
    doctorsData?.filter((doctor) => {
      return doctor.availability.some((avail) => {
        return avail.slots.some((slot) => {
          const currentDateTime = new Date();
          const slotDateTime = new Date(slot.datetime);
          return slotDateTime > currentDateTime;
        });
      });
    }) || [];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const sendEmail = () => {
    window.location.href = "mailto:contact@shvesa.com?subject=Payment Confirmation&body=Please find attached the screenshot of my payment.";
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isError) {
    return <div>Error fetching doctors</div>;
  }

  if (isSubmitting) {
    return <LoadingScreen />;
  }

  return (
    <div className="max-w-3xl mt-6 mx-0 md:mx-6">
      <div className="rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-medium mb-2">Book an Appointment</h2>
          <p className="text-sm text-gray-500">
            All times are in {currentTimeZone} time zone.
          </p>
          <p className="text-md text-green-600 font-semibold mt-2">
            Special Offer: Book 4 sessions and get the 5th session free!
          </p>
        </div>

        {submissionComplete ? (


          submissionComplete && showPaymentModal && (
            <BookingReceiptModal
              totalCost={totalCost}
              showBookingId={showBookingId}
              selectedDoctor={selectedDoctor}
              selectedSlots={selectedSlots}
              filteredDoctorsData={filteredDoctorsData}
              closeModal={closeModal}
              copyToClipboard={copyToClipboard}
              sendEmail={sendEmail}
            />
          )
        ) : (
          <>
            <h4 className="text-lg font-medium mb-4">Our Doctors</h4>
            <div className="space-y-6">
              {filteredDoctorsData?.length === 0 && (
                <div className="text-center text-gray-500">
                  No doctors available at the moment, please try again later
                </div>
              )}

              {filteredDoctorsData?.map((doctor) => (
                <div
                  key={doctor.id}
                  className={`bg-white shadow-md rounded-md p-4 border border-gray-200 ${selectedDoctor && selectedDoctor !== doctor.id
                      ? "opacity-50 hover:opacity-100"
                      : ""
                    }`}
                >
                  <div className="flex items-center space-x-4 mb-4">

                    {
                      (doctor.profile !== null && doctor.profile === "") && (
                        <div className="flex-shrink-0">
                          <img
                            className="h-12 w-12 rounded-full border"
                            src={doctor.profile}
                            alt={doctor.therapist.name}
                          />
                        </div>
                      )
                    }

                    <div>
                      <div className="flex gap-1">
                        <h4 className="text-lg font-medium text-gray-900">
                          {`${doctor.therapist.name}`}
                        </h4>

                        <span />
                        <FaCrown size={22} color="#C9A74A" />
                      </div>
                      <h2 className="text-md font-300 text-gray-500">
                        {doctor.specialities}
                      </h2>
                      <p className="text-lg text-gray-900 font-bold">
                        £{doctor.price} / session
                      </p>
                      {doctorFreeSlots[doctor.id] > 0 && (
                        <p className="text-sm text-green-600 font-semibold">
                          {doctorFreeSlots[doctor.id]} free slot{doctorFreeSlots[doctor.id] > 1 ? 's' : ''} available
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">
                      {doctor.therapist.specialty}
                    </p>
                  </div>
                  <div className="mb-4">
  <div className="grid grid-cols-7 gap-2">
    {getNext7Days().map((date) => {
      const dateObj = new Date(date);
      const dateNumber = dateObj.getDate();
      const monthName = dateObj.toLocaleString('default', { month: 'short' });
      const weekday = dateObj.toLocaleString('default', { weekday: 'short' });
      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;

      return (
        <button
          key={date}
          className={`px-2 py-4 rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary hover:text-white focus:ring-offset-2 transition-colors duration-300 border-2 flex flex-col items-center ${selectedDate === date
              ? "bg-primary text-white"
              : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          onClick={() => setSelectedDate(date)}
        >
          <span className={`text-2xl font-bold ${isWeekend && 'text-red-500'}`}>{dateNumber}</span>
          <span className={`text-md uppercase ${isWeekend && 'text-red-500'}`}>{monthName}</span>
          <span className={`text-xs uppercase ${isWeekend && 'text-red-500'}`}>
            {weekday}
          </span>
        </button>
      );
    })}
  </div>
  {selectedDate && (
    <div className="mt-6 grid grid-cols-3 gap-2">
      {modifiyAvailabilityList(doctor.availability)
        .find((avail) => avail.date === selectedDate)
        ?.slots.map((slot) => {
          const slotDateTime = new Date(slot.datetime);
          const formattedTime = slotDateTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          const freeAllocation = freeSlotAllocations.find(
            (allocation) => allocation.doctor.id === doctor.id && allocation.remaining_slots > 0
          );

          return (
            <button
              key={slot.id}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors duration-300 border-2 ${selectedSlots[selectedDate]?.some((s) => s.id === slot.id)
                  ? "bg-primary text-white"
                  : freeAllocation
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              onClick={() => handleSlotSelection(doctor, selectedDate, slot)}
            >
              {formattedTime}
              {freeAllocation && <span className="ml-1 text-xs">(Free)</span>}
            </button>
          );
        })}
    </div>
  )}
</div>
{selectedDoctor === doctor.id && Object.keys(selectedSlots).length > 0 && (
        <div
          ref={detailsRef}
          className="mt-4 border border-gray-200 rounded-md p-4"
        >
          <div className="mb-4">
            <h5 className="text-lg font-medium mb-2">
              Order Summary
            </h5>
            <div className="bg-gray-100 p-4 rounded-md">
              <p className="text-gray-600">
                Doctor: {doctor.therapist.name}
              </p>
              {Object.entries(selectedSlots).map(([date, slots]) => (
                <div key={date} className="mb-4">
                  <p className="text-gray-600">
                    Date: {date}
                  </p>
                  <ul className="list-disc pl-5 text-gray-600">
                    {slots.map((slot) => {
                      const slotDateTime = new Date(slot.datetime);
                      const formattedTime =
                        slotDateTime.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        });
                      return <li key={slot.id}>{formattedTime}</li>;
                    })}
                  </ul>
                </div>
              ))}
              {Object.values(selectedSlots).flat().length === 4 && (
                <p className="text-sm text-green-600 font-semibold mt-2 mb-2">
                  You've selected 4 slots. Your next slot will be free!
                </p>
              )}
              <div className="mt-4 border-t border-gray-300 pt-4 flex justify-between items-center">
                <p className="text-gray-600">Total Cost: </p>
                <p className="text-lg font-medium text-gray-800">
                  {isFreeSlot ? (
                    <span className="text-green-600">Free</span>
                  ) : (
                    `£${totalCost}`
                  )}
                </p>
              </div>
              {Object.values(selectedSlots).flat().length >= 5 && (
                <p className="text-sm text-green-600 mt-2">
                  Your 5th session is free!
                </p>
              )}
            </div>
          </div>
          <div className="w-full text-right">
            <button
              className="flex justify-center items-center gap-2 px-4 py-2 text-lg font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              onClick={handleSubmit}
            >
              {isFreeSlot ? "Book Free Slot" : "Book Appointment"}
              <FaArrowRight size={20} />
            </button>
          </div>
        </div>
      )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingStepper;
